import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, put, del } from '@/lib/api'

// ==================== Types ====================

export type AccountType = 'cash' | 'bank' | 'digital'

export interface Account {
  id: string
  name: string
  type: AccountType
  currency: string
  initialBalance: number
  isActive: boolean
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface AccountWithBalance extends Account {
  currentBalance: number
}

export interface CreateAccountInput {
  name: string
  type: AccountType
  currency?: string
  initialBalance?: number
}

export interface UpdateAccountInput {
  name?: string
  type?: AccountType
  currency?: string
}

// ==================== Query Keys ====================

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: () => [...accountKeys.lists()] as const,
  detail: (id: string) => [...accountKeys.all, 'detail', id] as const,
}

// ==================== Hooks ====================

/**
 * 获取账户列表（含余额）
 */
export function useAccounts() {
  return useQuery({
    queryKey: accountKeys.list(),
    queryFn: () => get<AccountWithBalance[]>('/finance/accounts'),
  })
}

/**
 * 获取账户详情
 */
export function useAccount(id: string) {
  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: () => get<AccountWithBalance>(`/finance/accounts/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建账户
 */
export function useCreateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAccountInput) => post<Account>('/finance/accounts', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() })
    },
  })
}

/**
 * 更新账户
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAccountInput }) =>
      put<Account>(`/finance/accounts/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() })
    },
  })
}

/**
 * 停用账户
 */
export function useDeactivateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/finance/accounts/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.lists() })
    },
  })
}
